import clsx from "clsx";
import type { ReactNode } from "react";

/**
 * Una métrica: etiqueta corta arriba, valor abajo (rejilla de la ficha de jugador).
 *
 * EL VALOR VA EN MONOESPACIADA
 *   Con `tabular-nums` los dígitos ocupan lo mismo y las tres columnas de la rejilla cuadran
 *   aunque los números cambien. Es la misma razón por la que las tablas de M8 usan mono.
 *
 * EL TONO NO ES DECORACIÓN
 *   `up` y `down` solo se usan cuando el signo del dato ya lo dice —la cifra lleva `+` o `-`—,
 *   de modo que el color confirma, nunca informa por su cuenta.
 */

export type MetricTone = "neutral" | "up" | "down" | "money";

const TONES: Record<MetricTone, string> = {
  neutral: "text-ink",
  up: "text-up",
  down: "text-down",
  money: "text-money",
};

export interface MetricBadgeProps {
  /** Etiqueta corta: *Pts/jorn.*, *Eficiencia*, *Tend. 7d*. */
  label: string;
  value: ReactNode;
  tone?: MetricTone;
  /** Explicación larga, para quien no reconozca la abreviatura. */
  title?: string;
  className?: string;
}

export function MetricBadge({
  label,
  value,
  tone = "neutral",
  title,
  className,
}: MetricBadgeProps) {
  return (
    <div
      title={title}
      className={clsx(
        "rounded-[9px] border border-border bg-white/[0.025] p-2",
        className,
      )}
    >
      <div className="text-[9.5px] font-bold uppercase tracking-[0.09em] text-muted">
        {label}
      </div>
      <div
        className={clsx(
          "mt-0.5 font-mono text-[15px] tabular-nums",
          TONES[tone],
        )}
      >
        {value}
      </div>
    </div>
  );
}

/** Rejilla de tres columnas para agrupar métricas, la del mockup de la ficha. */
export function MetricGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("grid grid-cols-3 gap-2", className)}>{children}</div>
  );
}
